import { useEffect, useState } from 'react';
import { Form, FormInstance } from 'antd';

import { useFileUpload } from '@hooks/shared/useFileUpload/useFileUpload';

type UseJobsFormReturn = {
  columnIdLoading: { [columnId: number]: boolean };
  formInstance: FormInstance;
  uploadedImages: { imgSrc: string; columnId: number }[];
  applicationResponses: ApplicationResponse[];
  customResponses: QuestionResponse[];
  handleInputChange: HandleJobsFormChange;
  handleFileChange: (info, columnId: number, questionLabel: string) => void;
  handleRemoveImage: (
    itemToRemove: {
      imgSrc: string;
      columnId: number;
    },
    isMiscAttachment?: boolean
  ) => void;
};

export const useJobsForm = (
  useBookingColumnId = false,
  initialFormData = {},
  initialUploadedImages = []
): UseJobsFormReturn => {
  const [formInstance] = Form.useForm();
  const [columnIdLoading, setColumnIdLoading] = useState({});
  const [formData, setFormData] = useState(initialFormData || {});
  const [formDataCustom, setFormDataCustom] = useState({});
  const [uploadedImages, setUploadedImages] = useState<
    { imgSrc: string; columnId: number }[]
  >(initialUploadedImages || []);
  const [applicationResponses, setApplicationResponses] = useState<
    ApplicationResponse[]
  >([]);

  const [customResponses, setCustomResponses] = useState<QuestionResponse[]>(
    []
  );

  const handleInputChange = (
    form: FormInstance,
    columnId: number,
    value: (string | number) | (string | number)[],
    questionLabel: string,
    isMiscQuestion?: boolean
  ) => {
    const getMultipleChoiceResponse = (prevState) => ({
      ...prevState,
      [columnId]: {
        ...prevState[columnId],
        ...(useBookingColumnId
          ? { booking_column_id: columnId }
          : { list_column_id: columnId }),
        response: {
          value: Array.isArray(value) ? value : [value],
        },
      },
    });
    const getSingleChoiceResponse = (prevState) => ({
      ...prevState,
      [columnId]: {
        ...prevState[columnId],
        ...(useBookingColumnId
          ? { booking_column_id: columnId }
          : { list_column_id: columnId }),
        response: {
          ...prevState[columnId]?.response,
          value: value,
        },
      },
    });
    if (formData[columnId]?.type === 'Multiple-choice') {
      // For multiple-choice questions, store the response as an array
      if (isMiscQuestion) {
        setFormDataCustom(getMultipleChoiceResponse);
      } else setFormData(getMultipleChoiceResponse);
      form.setFieldValue(questionLabel, [value]);
    } else {
      // Single-choice
      if (isMiscQuestion) {
        setFormDataCustom(getSingleChoiceResponse);
      } else setFormData(getSingleChoiceResponse);
      form.setFieldValue(questionLabel, value);
    }
  };

  const handleQuestionAttachment = (
    columnId: number,
    value: string,
    isMiscAttachment?: boolean
  ) => {
    const getUpdatedAttachmentFormData = (prevFormData) => {
      const updatedFormData = { ...prevFormData };
      const existingResponse = updatedFormData[columnId]?.response?.value || [];
      if (!existingResponse?.includes(value)) {
        existingResponse?.push(value);
        updatedFormData[columnId] = {
          ...updatedFormData[columnId],
          ...(useBookingColumnId
            ? { booking_column_id: columnId }
            : { list_column_id: columnId }),
          response: {
            value: existingResponse,
          },
        };
      }
      return updatedFormData;
    };

    if (isMiscAttachment) {
      setFormDataCustom(getUpdatedAttachmentFormData);
    } else {
      setFormData(getUpdatedAttachmentFormData);
    }
  };

  const callbackFn = (
    columnId: number,
    uploadUrl: UploadUrl,
    questionLabel: string,
    isMiscAttachment?: boolean
  ) => {
    const imgSrc = `https://dok4mt793oqq5.cloudfront.net/${uploadUrl.fields.key}`;
    setUploadedImages((prevState) => [...prevState, { imgSrc, columnId }]);
    handleQuestionAttachment(columnId, imgSrc, isMiscAttachment);
    formInstance.setFieldValue(questionLabel, imgSrc);
    setColumnIdLoading((prevState) => ({ ...prevState, [columnId]: false }));
  };

  const { uploadFile } = useFileUpload();

  const handleFileChange = async (
    info,
    columnId: number,
    questionLabel: string,
    isMiscAttachment?: boolean
  ) => {
    setColumnIdLoading((prevState) => ({ ...prevState, [columnId]: true }));
    await uploadFile(info.file, (uploadUrl) =>
      callbackFn(columnId, uploadUrl, questionLabel, isMiscAttachment)
    );
  };

  const handleRemoveImage = (
    itemToRemove: {
      imgSrc: string;
      columnId: number;
    },
    isMiscAttachment?: boolean
  ) => {
    const getUpdatedAttachmentFormData = (prevState) => {
      const updatedFormDataCustom = { ...prevState };
      const imageToDelete = updatedFormDataCustom[itemToRemove?.columnId];

      if (imageToDelete) {
        if (Array.isArray(imageToDelete.response.value)) {
          const updatedValue = imageToDelete.response.value.filter(
            (img) => img !== itemToRemove.imgSrc
          );
          imageToDelete.response.value = updatedValue;
        } else {
          imageToDelete.response.value = null;
        }
      }

      return updatedFormDataCustom;
    };

    if (isMiscAttachment) {
      setFormDataCustom(getUpdatedAttachmentFormData);
    } else setFormData(getUpdatedAttachmentFormData);

    // Remove the image
    setUploadedImages((prevUploadedImages) =>
      prevUploadedImages.filter((img) => img.imgSrc !== itemToRemove.imgSrc)
    );
  };

  useEffect(() => {
    const generateApplicationResponses = () => {
      const updatedResponses: ApplicationResponse[] = Object.keys(formData).map(
        (list_column_id) => ({
          list_column_id: +list_column_id,
          response: {
            value: formData[list_column_id].response
              .value as ApplicationResponseValue,
          },
        })
      );

      setApplicationResponses(updatedResponses);
    };
    generateApplicationResponses();
  }, [formData]);

  useEffect(() => {
    const generateCustomQuestionResponses = () => {
      const updatedResponses = Object?.keys(formDataCustom).map((columnId) => ({
        ...(useBookingColumnId
          ? { booking_column_id: +columnId }
          : { list_column_id: +columnId }),
        response: {
          value: formDataCustom[columnId].response.value,
        },
      }));
      setCustomResponses(updatedResponses);
    };
    generateCustomQuestionResponses();
  }, [useBookingColumnId, formDataCustom]);

  return {
    columnIdLoading,
    formInstance,
    uploadedImages,
    applicationResponses,
    customResponses,
    handleInputChange,
    handleFileChange,
    handleRemoveImage,
  };
};
